import { z } from "zod"
import { useForm, zodResolver } from "@mantine/form"
import { TextInput, Button, Group, MultiSelect, LoadingOverlay, Select } from "@mantine/core"
import { Icon } from "@material-tailwind/react"
import { create, update } from "services/api.service"
import useFetchAndLoad from "hooks/useFetchAndLoad.ts"

const UserForm = ({ data, refresh, isCreation, notifications, endpoint }) => {
  const { loading, callEndpoint } = useFetchAndLoad()
  const responseWorksites = localStorage.getItem("worksites")
  const worksitesSelectData = JSON.parse(responseWorksites).map((worksite) => ({
    value: worksite._id,
    label: worksite.name,
  }))

  const accessLevels = [
    { value: "1", label: "Administrador" },
    { value: "2", label: "Guardia" },
  ]

  const schema = z.object({
    name: z.string().min(2, { message: "Name should have at least 2 letters" }),
    lastName: z.string().min(2, { message: "El apellido debe tener al menos 2 letras" }),
    accessLevel: z.string().nonempty({ message: "Debes seleccionar un nivel de permiso" }),
    password: isCreation
      ? z.string().min(6, { message: "La contraseña debe tener al menos 6 caracteres" })
      : z.string().optional(),
  })

  const form = useForm({
    schema: zodResolver(schema),
    initialValues: {
      name: data.first_name,
      lastName: data.last_name,
      email: data.email,
      accessLevel: `${data.access_level}`,
      worksite: data.worksite ? `${data.worksite._id}` : null,
      password: "",
    },
  })

  const handleSubmit = async (values) => {
    const { name, lastName, email, accessLevel, worksite, password } = values
    const user = {
      ...data,
      first_name: name,
      last_name: lastName,
      email: email,
      access_level: accessLevel ? parseInt(accessLevel) : "",
      worksite: worksite !== undefined && worksite !== "undefined" ? worksite : null,
      password: password,
    }

    if (accessLevel < 2) {
      delete user.worksite
    }

    if (accessLevel == 2 && !user.worksite) {
      notifications.showNotification({
        color: "red",
        title: "Error.",
        message: "Necesitas seleccionar una obra para los usuarios con nivel de acceso de guardia.",
        icon: <Icon name={"close"} />,
      })
      return
    }

    if (data._id) {
      await callEndpoint(update(endpoint, user))
        .then((response) => {
          if (response.status === 200 || response.status === 201) {
            refresh()
            notifications.showNotification({
              color: "green",
              title: "Usuario actualizado",
              message: "El usuario ha sido actualizado con éxito.",
              icon: <Icon name={"check"} />,
            })
          }
        })
        .catch((error) => {
          showErrorNotification()
        })

      return
    }

    await callEndpoint(create(`${endpoint}/register`, user))
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          refresh()
          notifications.showNotification({
            color: "green",
            title: "Usuario creado",
            message: "El usuario ha sido creado con éxito.",
            icon: <Icon name={"check"} />,
          })
        }
      })
      .catch((error) => {
        showErrorNotification()
      })
  }

  const showErrorNotification = () => {
    notifications.showNotification({
      color: "red",
      title: "Error, por favor intentelo nuevamente.",
      message: "Si el problema persiste contactar a soporte.",
      icon: <Icon name={"close"} />,
    })
  }

  return (
    <>
      {loading && <LoadingOverlay visible={true} />}
      <form
        onSubmit={form.onSubmit(async (values) => {
          await handleSubmit(values)
        })}>
        <TextInput
          required
          label='Nombre(s)'
          placeholder='Nombre(s)'
          {...form.getInputProps("name")}
        />
        <TextInput
          required
          label='Apellidos'
          placeholder='Apellidos'
          mt='sm'
          {...form.getInputProps("lastName")}
        />
        <TextInput
          mt='sm'
          disabled={!isCreation}
          required
          label='Email'
          placeholder='ejemplo@mail.com'
          type='email'
          {...form.getInputProps("email")}
        />

        <Select
          data={accessLevels}
          label='Nivel de permisos del usuario'
          placeholder='Selecciona el nivel de permisos del usuario'
          mt='sm'
          required
          {...form.getInputProps("accessLevel")}
        />

        <Select
          data={worksitesSelectData}
          label='Obra'
          placeholder='Selecciona una obra'
          required={form.getInputProps("accessLevel").value == 1 ? false : true}
          clearable={form.getInputProps("accessLevel").value == 1 ? true : false}
          error={
            form.getInputProps("accessLevel").value && !form.getInputProps("worksite").value
              ? form.getInputProps("accessLevel").value == 1
                ? false
                : true
              : false
          }
          mt='sm'
          {...form.getInputProps("worksite")}
        />

        {isCreation && (
          <TextInput
            required
            label='Contraseña'
            placeholder='Contraseña'
            mt='sm'
            type='password'
            {...form.getInputProps("password")}
          />
        )}

        <Group position='center' mt='xl'>
          <Button className='bg-gray-700 text-white hover:bg-gray-600' type='submit'>
            {isCreation ? "Crear Usuario" : "Actualizar Usuario"}
          </Button>
        </Group>
      </form>
    </>
  )
}

export default UserForm
